import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import { useBreakReason } from "../../../Hooks/useBreakReason";
import httpclient from "../../../Utils";

const BreakReasonModal = ({ open, onClose, mode, reasonData, onSuccess }) => {
  const [reasonInfo, setReasonInfo] = useState({
    reason_name: "", // Changed from 'name' to 'reason_name' to match API
    status: 0, // Changed from 'isReasonActive' boolean to status number (0/1)
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { addBreakReason, updateBreakReason } = useBreakReason();

  useEffect(() => {
    if (mode === "edit" && reasonData) {
      setReasonInfo({
        reason_name: reasonData.reason_name,
        status: reasonData.status, // Keep as number (1 or 0)
      });
    } else {
      setReasonInfo({
        reason_name: "",
        status: 0, // Default to inactive (0)
      });
    }
  }, [mode, reasonData, open]);

  const handleReasonInfoChange = (event) => {
    const { name, value } = event.target;
    setReasonInfo((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleStatusChange = (event) => {
    setReasonInfo((prevData) => ({
      ...prevData,
      status: event.target.checked ? 1 : 0, // Convert boolean to 1/0
    }));
  };

  const validateForm = () => {
    if (!reasonInfo.reason_name.trim()) {
      showNotification("error", "Break Reason Name is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      
      let result;
      if (mode === "edit") {
        result = await updateBreakReason(reasonData.id, reasonInfo);
      } else {
        result = await addBreakReason(reasonInfo);
      }

      if (result.success) {
        onSuccess(); // Refresh the list
        onClose(); // Close the modal
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {mode === "edit" ? "Edit Break Reason" : "Add New Break Reason"}
      </DialogTitle>
      
      <DialogContent dividers>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography
            component="div"
            color="secondary"
            variant="caption"
            sx={{ mb: 2, fontStyle: "italic" }}
          >
            Field marked * is required
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="reason_name"
                label="Break Reason Name"
                name="reason_name"
                value={reasonInfo.reason_name}
                onChange={handleReasonInfoChange}
                variant="outlined"
                autoFocus
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={reasonInfo.status === 1}
                    onChange={handleStatusChange}
                    name="status"
                    color="primary"
                  />
                }
                label={
                  reasonInfo.status === 1
                    ? "Reason is Active"
                    : "Reason is Inactive"
                }
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: "none" }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ 
            backgroundColor: "#7152F3",
            textTransform: "none",
            "&:hover": { backgroundColor: "#5c34fa" }
          }}
          disabled={isSubmitting}
        >
          {isSubmitting && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
          {mode === "edit" ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BreakReasonModal;